// WorkspaceContexts.tsx
import * as React from 'react'
import {Workspace} from 'types'

export type WorkspaceStateShape = {
	favorites: Set<string>
	isWorkspaceLoading: boolean
}
export type WorkspaceActions = {
	setFavorites: (id: string) => void
	setFavoritesFromData: (data: Workspace[]) => void
	setIsWorkspaceLoading: (v: boolean) => void
}

export const WorkspaceStateContext =
	React.createContext<WorkspaceStateShape | null>(null)
export const WorkspaceActionsContext =
	React.createContext<WorkspaceActions | null>(null)

export const useWorkspaceState = () => {
	const ctx = React.useContext(WorkspaceStateContext)
	if (!ctx)
		throw new Error('useWorkspaceState must be used within WorkspaceProvider')
	return ctx
}
export const useWorkspaceActions = () => {
	const ctx = React.useContext(WorkspaceActionsContext)
	if (!ctx)
		throw new Error('useWorkspaceActions must be used within WorkspaceProvider')
	return ctx
}

// Provider
export const WorkspaceProvider: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	const [favorites, setFavoritesState] = React.useState<Set<string>>(new Set())
	const [isWorkspaceLoading, setIsWorkspaceLoading] = React.useState(false)

	const setFavorites = React.useCallback((id: string) => {
		setFavoritesState(prev => {
			const next = new Set(prev)
			next.has(id) ? next.delete(id) : next.add(id)
			return next
		})
	}, [])

	const setFavoritesFromData = React.useCallback((data: Workspace[]) => {
		setFavoritesState(new Set(data.filter(w => w.isFavorite).map(w => w.id)))
	}, [])

	const actions = React.useMemo<WorkspaceActions>(
		() => ({
			setFavorites,
			setFavoritesFromData,
			setIsWorkspaceLoading,
		}),
		[setFavorites, setFavoritesFromData],
	)

	const state = React.useMemo<WorkspaceStateShape>(
		() => ({
			favorites,
			isWorkspaceLoading,
		}),
		[favorites, isWorkspaceLoading],
	)

	return (
		<WorkspaceStateContext.Provider value={state}>
			<WorkspaceActionsContext.Provider value={actions}>
				{children}
			</WorkspaceActionsContext.Provider>
		</WorkspaceStateContext.Provider>
	)
}
